import { UntypedFormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { ControlProperty } from '../../../../../../common/models';
import { DayOfWeek, TIMEPICKER_DISPLAY_FORMAT } from '../../../../../../common/constants';
import { isNaN, isNil } from 'lodash';
import { LocalDate, LocalDateModel } from '../../../../../models';
import { getTimestampWithTimezoneOffset, getTimezoneOffsetInHours } from '../../../../../../common/utils';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

export class WorkingDayModel<T = number> {
  start: T = null;
  end: T = null;

  constructor(params?: WorkingDayModel<T>) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class WorkingDayUIModel {
  start: string = null;
  end: string = null;

  constructor(params?: WorkingDayUIModel) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class WorkingDayForm {
  constructor(
    model: WorkingDayUIModel,
    public start: ControlProperty<string> = [model.start, []],
    public end: ControlProperty<string> = [model.end, []],
  ) {}
}

export class WorkingHoursResponseModel {
  calendars: WorkingHoursBrief[];
  timezone: string;
}

export class WorkingHoursBrief {
  id?: number;
  validFrom: number;

  constructor(params?: WorkingHoursBrief) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class WorkingHoursViewCalendarModel {
  validFrom: LocalDateModel;
  workingHours: WorkingHoursCalendarModel[];
}

export class WorkingHoursCalendarModel {
  start: LocalDate;
  end: LocalDate;
}

export class WorkingHoursModel extends WorkingHoursBrief {
  monday: WorkingDayModel[] = [];
  tuesday: WorkingDayModel[] = [];
  wednesday: WorkingDayModel[] = [];
  thursday: WorkingDayModel[] = [];
  friday: WorkingDayModel[] = [];
  saturday: WorkingDayModel[] = [];
  sunday: WorkingDayModel[] = [];
  timezone: string = null;

  constructor(params?: WorkingHoursModel) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class WorkingHoursUIModel extends WorkingHoursBrief {
  monday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  tuesday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  wednesday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  thursday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  friday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  saturday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  sunday: WorkingDayUIModel[] = [new WorkingDayUIModel()];
  timezone: string = null;

  constructor(params?: WorkingHoursUIModel) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class WorkingHoursForm {
  constructor(
    model: WorkingHoursModel | WorkingHoursUIModel,
    fb: UntypedFormBuilder,
    public id: ControlProperty<number> = model.id,
    public validFrom: ControlProperty<number> = [model.validFrom, [Validators.required]],
    public timezone: ControlProperty<string> = model.timezone,
    public monday: UntypedFormArray = fb.array([]),
    public tuesday: UntypedFormArray = fb.array([]),
    public wednesday: UntypedFormArray = fb.array([]),
    public thursday: UntypedFormArray = fb.array([]),
    public friday: UntypedFormArray = fb.array([]),
    public saturday: UntypedFormArray = fb.array([]),
    public sunday: UntypedFormArray = fb.array([]),
  ) {
    Object.values(DayOfWeek).forEach((day: DayOfWeek) => {
      const dayAsKey: string = day.toLowerCase();
      const dayTimes: WorkingDayUIModel[] = model[dayAsKey];
      dayTimes.forEach((time: WorkingDayUIModel) => {
        this[dayAsKey].push(fb.group(new WorkingDayForm(new WorkingDayUIModel(time))));
      });
    });
  }
}

export class WorkingDayModelToUIModel extends WorkingDayUIModel {
  constructor({ start, end }: WorkingDayModel, timeZone: string) {
    super();

    const safeTimeZone = typeof timeZone === 'string' && timeZone.trim().length > 0 ? timeZone : 'UTC';

    if (!isNil(start)) {
      this.start = formatInTimeZone(start, safeTimeZone, TIMEPICKER_DISPLAY_FORMAT);
    }

    if (!isNil(end)) {
      this.end = formatInTimeZone(end, safeTimeZone, TIMEPICKER_DISPLAY_FORMAT);
    }
  }
}

export class WorkingDayUIModelToTimestampModel extends WorkingDayModel {
  constructor({ start, end }: WorkingDayUIModel, validFrom: number, timezone: string) {
    super();

    const safeTimeZone = typeof timezone === 'string' && timezone.trim().length > 0 ? timezone : 'UTC';

    const getZonedTimestamp = (timeString: string): number => {
      const baseDate = new Date(validFrom);
      if (isNaN(baseDate.getTime())) {
        console.error('Invalid base date:', validFrom);
        return null;
      }

      const year = baseDate.getUTCFullYear();
      const month = String(baseDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(baseDate.getUTCDate()).padStart(2, '0');

      const isoDateTimeString = `${year}-${month}-${day}T${timeString}:00`;

      const zonedDate = fromZonedTime(isoDateTimeString, safeTimeZone);

      return zonedDate.valueOf();
    };

    if (start) {
      const calculatedStart = getZonedTimestamp(start);
      if (calculatedStart !== null) {
        this.start = calculatedStart;
      }
    }

    if (end) {
      const calculatedEnd = getZonedTimestamp(end);
      if (calculatedEnd !== null) {
        this.end = calculatedEnd;
      }
    }
  }
}

export class WorkingHoursTimestampToFieldFormat extends WorkingHoursUIModel {
  constructor(params?: WorkingHoursModel) {
    super();
    if (!params) {
      return;
    }
    const timezoneOffset = getTimezoneOffsetInHours(params?.timezone);

    Object.assign(this, params);
    this.validFrom = getTimestampWithTimezoneOffset(params.validFrom, -timezoneOffset);
    Object.values(DayOfWeek).forEach((day: DayOfWeek) => {
      const dayAsKey: string = day.toLowerCase();
      const dayTimes: WorkingDayModel[] = [...params[dayAsKey]];
      this[dayAsKey] = dayTimes.map((time: WorkingDayModel) => {
        const timeWithoutOffset = {
          start: time.start,
          end: time.end,
        };

        return new WorkingDayModelToUIModel(timeWithoutOffset, params?.timezone);
      });
    });
  }
}

export class WorkingHoursFieldFormatToTimestamp extends WorkingHoursModel {
  constructor(params?: WorkingHoursUIModel) {
    super();
    if (!params) {
      return;
    }
    const timezone = params?.timezone;
    Object.assign(this, params);

    if (params.validFrom && timezone) {
      this.validFrom = fromZonedTime(params.validFrom, timezone).valueOf();
    } else if (params.validFrom) {
      this.validFrom = new Date(params.validFrom).valueOf();
    }

    Object.values(DayOfWeek).forEach((day: DayOfWeek) => {
      const dayAsKey: string = day.toLowerCase();
      const dayTimes: WorkingDayUIModel[] = [...params[dayAsKey]];
      this[dayAsKey] = dayTimes.map((time: WorkingDayUIModel) => {
        return new WorkingDayUIModelToTimestampModel(time, this.validFrom, timezone);
      });
    });
  }
}
